import axios from "axios";
import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import config from "../config/config";
import { toast } from "react-toastify";
import { useAuth } from "../pages/login/AuthContext";
import { useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import { AgGridReact } from "ag-grid-react";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import { AgGridSkeleton } from "./DataTableSkeleton";
import { localeText } from "./DataTable/localtext";
import DeleteConfirmDialog from "./DataTable/modals/DeleteConfirmDialog";
import ForwardConfirmDialog from "./DataTable/modals/ForwardConfirmDialog";

ModuleRegistry.registerModules([AllCommunityModule]);

export const DataTable = forwardRef(
  (
    {
      searchText,
      tableType = "orders",
      columnDefs: propColumnDefs,
      apiEndpoint,
      searchEndpoint,
      onEditAction,
      onDeleteAction,
      onForwardAction,
      onRowDoubleClicked,
      editPath,
      deleteConfirmMessage,
      forwardConfirmMessage,
      idField = "Id",
      loading: externalLoading,
      onLoadingChange,
      customContext,
      customQueryParams = {},
      initialData,
      pageSize: propPageSize,
    },
    ref
  ) => {
    const [isLoading, setIsLoading] = useState(
      typeof externalLoading !== "undefined" ? externalLoading : false
    );
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [openForwardModal, setOpenForwardModal] = useState(false);
    const [selectedItemId, setSelectedItemId] = useState(null);

    const gridRef = useRef(null);
    const gridContainerRef = useRef(null);
    const customQueryParamsRef = useRef(customQueryParams);
    const searchTextRef = useRef(searchText);
    const searchDebounceTimeoutRef = useRef(null);
    const isInitialMount = useRef(true);
    const gridReadyFired = useRef(false);

    const { token, userData, hasPermission } = useAuth();
    const navigate = useNavigate();
    const [totalRecords, setTotalRecords] = useState(0);

    const isPaged = typeof propPageSize === "number" && propPageSize > 0;
    const pageSize = isPaged ? propPageSize : 50;
    const [pagedPage, setPagedPage] = useState(1);
    const [pagedRowData, setPagedRowData] = useState([]);

    // Custom params ve searchText değişikliklerini ref'te tut
    useEffect(() => {
      customQueryParamsRef.current = customQueryParams;
    }, [customQueryParams]);

    useEffect(() => {
      searchTextRef.current = searchText;
    }, [searchText]);

    useEffect(() => {
      if (typeof externalLoading !== "undefined") {
        setIsLoading(externalLoading);
      }
    }, [externalLoading]);

    const axiosInstance = useCallback(() => {
      return axios.create({
        baseURL: config.baseURL,
        headers: { Authorization: `Bearer ${token}` },
      });
    }, [token]);

    const columnDefs = useMemo(() => {
      if (propColumnDefs) {
        return propColumnDefs;
      }
      return [];
    }, [propColumnDefs]);

    const defaultColDef = useMemo(
      () => ({
        flex: 1,
        minWidth: 100,
        resizable: true,
        filterParams: {
          buttons: ["sıfırla", "uygula"],
        },
      }),
      []
    );

    // Handler fonksiyonları
    function handleEditItem(itemId) {
      if (onEditAction) {
        onEditAction(itemId);
      } else if (editPath) {
        navigate(`${editPath}/${itemId}`);
      }
    }

    function handleOpenDeleteModal(itemId) {
      setSelectedItemId(itemId);
      setOpenDeleteModal(true);
    }

    function handleOpenForwardModal(itemId) {
      if (!onForwardAction) return;
      setSelectedItemId(itemId);
      setOpenForwardModal(true);
    }

    const contextValues = useMemo(() => {
      const baseContext = {
        handleEditItem,
        handleEditOrder: handleEditItem,
        handleOpenDeleteModal,
        handleOpenForwardModal,
        userData,
        hasPermission,
        hasForwardOption: !!onForwardAction,
      };

      return customContext ? { ...baseContext, ...customContext } : baseContext;
    }, [userData, hasPermission, onForwardAction, customContext]);

    // Datasource oluşturma fonksiyonu - MERKEZI (customQueryParams closure'da; Aktif/Pasif geçişinde doğru param gider)
    const getDataSource = useCallback(() => {
      const api = axiosInstance();
      const capturedParams = customQueryParams || customQueryParamsRef.current;

      return {
        getRows: async (gridParams) => {
          const startRow = gridParams.startRow;
          const endRow = gridParams.endRow;

          try {
            const pageSize = endRow - startRow;
            const page = Math.floor(startRow / pageSize) + 1;

            // Sıralama parametrelerini hazırla
            const sortParams = {};
            if (gridParams.sortModel && gridParams.sortModel.length > 0) {
              const sortModel = gridParams.sortModel[0];
              sortParams.sortField = sortModel.colId;
              sortParams.sortOrder = sortModel.sort;
              sortParams.orderBy = sortModel.colId;
              sortParams.order = sortModel.sort;
            }

            // Filtreleme parametrelerini hazırla
            const filterParams = {};
            if (
              gridParams.filterModel &&
              Object.keys(gridParams.filterModel).length > 0
            ) {
              Object.keys(gridParams.filterModel).forEach((field) => {
                const filter = gridParams.filterModel[field];

                if (filter.filter !== undefined) {
                  filterParams[`filter_${field}`] = filter.filter;
                  filterParams[`filterType_${field}`] =
                    filter.type || "contains";
                }

                if (
                  filter.type === "equals" ||
                  filter.type === "notEqual" ||
                  filter.type === "lessThan" ||
                  filter.type === "greaterThan"
                ) {
                  filterParams[`filter_${field}`] = filter.filter;
                  filterParams[`filterType_${field}`] = filter.type;
                }

                if (filter.type === "inRange") {
                  filterParams[`filterFrom_${field}`] = filter.filter;
                  filterParams[`filterTo_${field}`] = filter.filterTo;
                  filterParams[`filterType_${field}`] = "inRange";
                }

                if (filter.type === "equals" && filter.dateFrom) {
                  filterParams[`date_${field}`] = filter.dateFrom;
                  filterParams[`filterType_${field}`] = "equals";
                }

                if (filter.type === "notEqual" && filter.dateFrom) {
                  filterParams[`date_${field}`] = filter.dateFrom;
                  filterParams[`filterType_${field}`] = "notEqual";
                }

                if (filter.type === "lessThan" && filter.dateFrom) {
                  filterParams[`dateTo_${field}`] = filter.dateFrom;
                  filterParams[`filterType_${field}`] = "lessThan";
                }

                if (filter.type === "greaterThan" && filter.dateFrom) {
                  filterParams[`dateFrom_${field}`] = filter.dateFrom;
                  filterParams[`filterType_${field}`] = "greaterThan";
                }

                if (
                  filter.type === "inRange" &&
                  filter.dateFrom &&
                  filter.dateTo
                ) {
                  filterParams[`dateFrom_${field}`] = filter.dateFrom;
                  filterParams[`dateTo_${field}`] = filter.dateTo;
                  filterParams[`filterType_${field}`] = "inRange";
                }

                if (filter.values && Array.isArray(filter.values)) {
                  filterParams[`filterValues_${field}`] =
                    filter.values.join(",");
                  filterParams[`filterType_${field}`] = "in";
                }

                if (filter.type === "blank") {
                  filterParams[`filterType_${field}`] = "blank";
                }

                if (filter.type === "notBlank") {
                  filterParams[`filterType_${field}`] = "notBlank";
                }
              });
            }

            // Arama parametresi - ref'ten güncel değeri al
            const currentSearchText = searchTextRef.current;
            const searchParams =
              currentSearchText?.length >= 3
                ? { query: currentSearchText }
                : {};
            const hasSearch = currentSearchText?.length >= 3;

            // Endpoint belirleme: arama varsa searchEndpoint, yoksa apiEndpoint
            const endpoint =
              hasSearch && searchEndpoint ? searchEndpoint : apiEndpoint;

            const requestParams = {
              page,
              limit: pageSize,
              ...sortParams,
              ...filterParams,
              ...searchParams,
              ...(capturedParams || {}),
            };

            // Backend isteği
            const res = await api.get(endpoint, {
              params: requestParams,
            });

            const rows = res.data?.data || res.data?.rows || [];
            const totalRows =
              res.data?.pagination?.total ||
              res.data?.totalCount ||
              res.data?.total ||
              res.data?.totalItems ||
              res.data?.pagination?.totalItems ||
              rows.length;
            setTotalRecords(totalRows);
            gridParams.successCallback(rows, totalRows);
          } catch (err) {
            console.error("🔴 Grid veri yükleme hatası:", err);
            gridParams.failCallback();
            toast.error("Veri yüklenirken hata oluştu");
          }
        },
      };
    }, [axiosInstance, apiEndpoint, searchEndpoint, customQueryParams]);

    const loadPagedData = useCallback(async () => {
      if (!isPaged || !gridRef.current) return;
      const api = axiosInstance();
      const capturedParams = customQueryParamsRef.current || {};
      const currentSearch = searchTextRef.current;
      const hasSearch = currentSearch?.length >= 3;
      const endpoint = hasSearch && searchEndpoint ? searchEndpoint : apiEndpoint;
      const sortParams = {};
      if (gridRef.current.getColumnState) {
        const state = gridRef.current.getColumnState();
        const sorted = state?.find((c) => c.sort != null);
        if (sorted) {
          sortParams.sortField = sorted.colId;
          sortParams.sortOrder = sorted.sort;
          sortParams.orderBy = sorted.colId;
          sortParams.order = sorted.sort;
        }
      }
      const requestParams = {
        page: pagedPage,
        limit: pageSize,
        ...sortParams,
        ...(hasSearch ? { query: currentSearch } : {}),
        ...capturedParams,
      };
      try {
        setIsLoading(true);
        const res = await api.get(endpoint, { params: requestParams });
        const rows = res.data?.data || res.data?.rows || res.data?.list || [];
        const total =
          res.data?.pagination?.total ??
          res.data?.totalCount ??
          res.data?.total ??
          res.data?.totalItems ??
          res.data?.pagination?.totalItems ??
          rows.length;
        setPagedRowData(rows);
        setTotalRecords(total);
      } catch (err) {
        console.error("Paged grid load error:", err);
        toast.error("Veri yüklenirken hata oluştu");
        setPagedRowData([]);
      } finally {
        setIsLoading(false);
      }
    }, [isPaged, pageSize, pagedPage, axiosInstance, apiEndpoint, searchEndpoint, searchText]);

    // Imperative handle (loadPagedData ve getDataSource tanımlandıktan sonra)
    useImperativeHandle(ref, () => ({
      refreshData: () => {
        if (isPaged) {
          loadPagedData();
          return;
        }
        if (gridRef.current?.purgeInfiniteCache) {
          gridRef.current.purgeInfiniteCache();
        }
        const newDataSource = getDataSource();
        if (gridRef.current?.setGridOption) {
          gridRef.current.setGridOption("datasource", newDataSource);
        } else if (gridRef.current?.setDatasource) {
          gridRef.current.setDatasource(newDataSource);
        }
      },
      purgeInfiniteCache: () => {
        if (gridRef.current?.purgeInfiniteCache) {
          gridRef.current.purgeInfiniteCache();
        }
      },
      getIsLoading: () => isLoading,
    }), [isPaged, loadPagedData, getDataSource, isLoading]);

    useEffect(() => {
      if (!isPaged) return;
      loadPagedData();
    }, [isPaged, pagedPage, loadPagedData]);

    // Arama veya filtre değişince 1. sayfaya dön (customQueryParams referansı her render yeni {} olmasın diye içeriğe göre karşılaştırıyoruz)
    const customQueryParamsSerialized = useMemo(
      () => (customQueryParams && Object.keys(customQueryParams).length > 0 ? JSON.stringify(customQueryParams) : ""),
      [customQueryParams]
    );
    useEffect(() => {
      if (!isPaged) return;
      setPagedPage(1);
    }, [isPaged, searchText, customQueryParamsSerialized]);

    const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
    const goFirst = () => setPagedPage(1);
    const goPrev = () => setPagedPage((p) => Math.max(1, p - 1));
    const goNext = () => setPagedPage((p) => Math.min(totalPages, p + 1));
    const goLast = () => setPagedPage(totalPages);

    // Grid hazır olduğunda - sadece bir kere çalışır
    const onGridReady = useCallback(
      (params) => {
        if (gridReadyFired.current) return;

        gridReadyFired.current = true;
        gridRef.current = params.api;

        if (isPaged) {
          loadPagedData();
        } else {
          const dataSource = getDataSource();
          if (params.api.setGridOption) {
            params.api.setGridOption("datasource", dataSource);
          } else if (params.api.setDatasource) {
            params.api.setDatasource(dataSource);
          }
        }
      },
      [getDataSource, isPaged, loadPagedData]
    );

    // Arama metni değiştiğinde grid'i yenile - İLK MOUNT'u ATLA
    useEffect(() => {
      if (isInitialMount.current || isPaged) return;
      if (!gridRef.current) return;

      // Önceki timeout'u temizle
      if (searchDebounceTimeoutRef.current) {
        clearTimeout(searchDebounceTimeoutRef.current);
      }

      searchDebounceTimeoutRef.current = setTimeout(() => {
        // Datasource'u yeniden oluştur ve ayarla
        const newDataSource = getDataSource();

        // AG Grid versiyonuna göre uyumlu metod kullan
        if (gridRef.current.setGridOption) {
          gridRef.current.setGridOption("datasource", newDataSource);
        } else if (gridRef.current.setDatasource) {
          gridRef.current.setDatasource(newDataSource);
        }
      }, 500); // Debounce 500ms

      return () => {
        if (searchDebounceTimeoutRef.current) {
          clearTimeout(searchDebounceTimeoutRef.current);
        }
      };
    }, [searchText, getDataSource]);

    // Custom query params değiştiğinde grid'i yenile ve cache'i temizle - İLK MOUNT'u ATLA
    useEffect(() => {
      if (isInitialMount.current || isPaged) return;
      if (!gridRef.current) return;
      const newDataSource = getDataSource();

      // AG Grid versiyonuna göre uyumlu metod kullan
      if (gridRef.current.setGridOption) {
        gridRef.current.setGridOption("datasource", newDataSource);
        if (gridRef.current.purgeInfiniteCache) {
          gridRef.current.purgeInfiniteCache();
        }
      } else if (gridRef.current.setDatasource) {
        gridRef.current.setDatasource(newDataSource);
        if (gridRef.current.purgeInfiniteCache) {
          gridRef.current.purgeInfiniteCache();
        }
      }
    }, [customQueryParams, getDataSource]);

    // apiEndpoint veya searchEndpoint değiştiğinde grid'i yenile
    useEffect(() => {
      if (isInitialMount.current || isPaged) return;
      if (!gridRef.current) return;
      const newDataSource = getDataSource();

      // AG Grid versiyonuna göre uyumlu metod kullan
      if (gridRef.current.setGridOption) {
        gridRef.current.setGridOption("datasource", newDataSource);
        // Cache'i temizle ve yeniden yükle
        if (gridRef.current.purgeInfiniteCache) {
          gridRef.current.purgeInfiniteCache();
        }
      } else if (gridRef.current.setDatasource) {
        gridRef.current.setDatasource(newDataSource);
        // Cache'i temizle ve yeniden yükle
        if (gridRef.current.purgeInfiniteCache) {
          gridRef.current.purgeInfiniteCache();
        }
      }
    }, [apiEndpoint, searchEndpoint, getDataSource]);

    // İlk mount kontrolünü grid hazır olduktan sonra kapat
    useEffect(() => {
      if (gridReadyFired.current && isInitialMount.current) {
        isInitialMount.current = false;
      }
    }, []);

    const handleCloseDeleteModal = () => {
      setOpenDeleteModal(false);
      setSelectedItemId(null);
    };

    const handleCloseForwardModal = () => {
      setOpenForwardModal(false);
      setSelectedItemId(null);
    };

    const handleDelete = async () => {
      try {
        if (onDeleteAction) {
          await onDeleteAction(selectedItemId);
        } else {
          const api = axiosInstance();
          const endpoint =
            tableType === "orders"
              ? `/siparis/delete/${selectedItemId}`
              : `/lisanslar/lisans-sil/${selectedItemId}`;

          await api.put(endpoint);
        }

        toast.success(
          `${
            tableType === "orders"
              ? "Sipariş"
              : tableType === "licenses"
              ? "Lisans"
              : "Kayıt"
          } başarıyla silindi`
        );

        // Grid'i tamamen yenile - cache'i temizle
        if (gridRef.current) {
          gridRef.current.purgeInfiniteCache();
        }

        handleCloseDeleteModal();
      } catch (error) {
        console.error("Silme hatası:", error);
        toast.error(
          `${
            tableType === "orders"
              ? "Sipariş"
              : tableType === "licenses"
              ? "Lisans"
              : "Kayıt"
          } silinemedi`
        );
      }
    };

    const handleForward = async () => {
      if (!onForwardAction) return;

      try {
        await onForwardAction(selectedItemId);
        toast.success("İşlem başarıyla tamamlandı");

        // Grid'i tamamen yenile
        if (gridRef.current) {
          gridRef.current.purgeInfiniteCache();
        }

        handleCloseForwardModal();
      } catch (error) {
        console.error("İşlem hatası:", error.response?.data);
        toast.error(error.response?.data || "İşlem tamamlanamadı");
      }
    };

    const gridOptions = useMemo(
      () => ({
        rowModelType: isPaged ? "clientSide" : "infinite",
        ...(isPaged ? {} : { cacheBlockSize: 50, maxBlocksInCache: 20 }),
        pagination: false,
        rowHeight: 48,
        getRowId: (params) => {
          return params.data?.[idField]?.toString() || Math.random().toString();
        },
        localeText,
      }),
      [idField, isPaged]
    );

    const headerText = columnDefs.map((col) => col.headerName);

    return (
      <div ref={gridContainerRef}>
        <style>
          {`
          .action-cell-center {
            display: flex !important;
            justify-content: center !important;
            align-items: center !important;
            padding: 0 !important;
          }

          .ag-theme-alpine .ag-cell {
            user-select: text !important;
            -webkit-user-select: text !important;
            -moz-user-select: text !important;
            -ms-user-select: text !important;
          }

          .ag-theme-alpine .ag-cell-value {
            user-select: text !important;
            -webkit-user-select: text !important;
            -moz-user-select: text !important;
            -ms-user-select: text !important;
          }

          .ag-theme-alpine .ag-header-cell-text {
            user-select: text !important;
            -webkit-user-select: text !important;
            -moz-user-select: text !important;
            -ms-user-select: text !important;
          }

          .ag-theme-tabStyle-5 {
              max-height: calc(100% - 49px) !important;
            }
            .ag-root-wrapper{
              border-radius:8px 8px 0 0;
            }
        `}
        </style>

        <div className="ag-theme-alpine table-container">
          {isLoading ? (
            <AgGridSkeleton
              rowCount={20}
              columnCount={columnDefs.length}
              headerText={headerText}
              showHeaders={false}
              headerLoadDelay={300}
            />
          ) : (
            <AgGridReact
              ref={gridRef}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              rowData={isPaged ? pagedRowData : undefined}
              onGridReady={onGridReady}
              onSortChanged={isPaged ? () => setPagedPage(1) : undefined}
              onRowDoubleClicked={onRowDoubleClicked}
              gridOptions={gridOptions}
              animateRows={false}
              suppressMultiSort={false}
              multiSortKey="ctrl"
              context={contextValues}
            />
          )}

          <div
            style={{
              minHeight: "49px",
              backgroundColor: "color-mix(in srgb, #fff, #181d1f 2%)",
              padding: "8px 12px",
              fontSize: "14px",
              fontWeight: "500",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 8,
              border: "solid 1px color-mix(in srgb, transparent, #181d1f 15%)",
              borderTop: "none",
              borderRadius: "0 0 8px 8px",
            }}
          >
            {isPaged ? (
              <>
                <span style={{ marginRight: 8 }}>
                  {totalRecords === 0
                    ? "0 kayıt"
                    : `${(pagedPage - 1) * pageSize + 1}-${Math.min(pagedPage * pageSize, totalRecords)} / ${totalRecords} kayıt`}
                </span>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <button
                    type="button"
                    onClick={goFirst}
                    disabled={pagedPage <= 1}
                    style={{
                      padding: "4px 10px",
                      cursor: pagedPage <= 1 ? "not-allowed" : "pointer",
                      opacity: pagedPage <= 1 ? 0.5 : 1,
                      border: "1px solid #ccc",
                      borderRadius: 4,
                      background: "#fff",
                      fontSize: 13,
                    }}
                  >
                    İlk
                  </button>
                  <button
                    type="button"
                    onClick={goPrev}
                    disabled={pagedPage <= 1}
                    style={{
                      padding: "4px 10px",
                      cursor: pagedPage <= 1 ? "not-allowed" : "pointer",
                      opacity: pagedPage <= 1 ? 0.5 : 1,
                      border: "1px solid #ccc",
                      borderRadius: 4,
                      background: "#fff",
                      fontSize: 13,
                    }}
                  >
                    Önceki
                  </button>
                  <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <label style={{ whiteSpace: "nowrap" }}>Sayfa</label>
                    <input
                      type="number"
                      min={1}
                      max={totalPages}
                      value={pagedPage}
                      onChange={(e) => {
                        const v = parseInt(e.target.value, 10);
                        if (!Number.isNaN(v) && v >= 1 && v <= totalPages) setPagedPage(v);
                      }}
                      style={{
                        width: 52,
                        padding: "4px 6px",
                        border: "1px solid #ccc",
                        borderRadius: 4,
                        fontSize: 13,
                        textAlign: "center",
                      }}
                    />
                    <span>/ {totalPages}</span>
                  </span>
                  <button
                    type="button"
                    onClick={goNext}
                    disabled={pagedPage >= totalPages}
                    style={{
                      padding: "4px 10px",
                      cursor: pagedPage >= totalPages ? "not-allowed" : "pointer",
                      opacity: pagedPage >= totalPages ? 0.5 : 1,
                      border: "1px solid #ccc",
                      borderRadius: 4,
                      background: "#fff",
                      fontSize: 13,
                    }}
                  >
                    Sonraki
                  </button>
                  <button
                    type="button"
                    onClick={goLast}
                    disabled={pagedPage >= totalPages}
                    style={{
                      padding: "4px 10px",
                      cursor: pagedPage >= totalPages ? "not-allowed" : "pointer",
                      opacity: pagedPage >= totalPages ? 0.5 : 1,
                      border: "1px solid #ccc",
                      borderRadius: 4,
                      background: "#fff",
                      fontSize: 13,
                    }}
                  >
                    Son
                  </button>
                </div>
              </>
            ) : (
              <span>Kayıt Sayısı: {totalRecords}</span>
            )}
          </div>
        </div>

        <DeleteConfirmDialog
          open={openDeleteModal}
          onClose={handleCloseDeleteModal}
          onConfirm={handleDelete}
          deleteConfirmMessage={deleteConfirmMessage}
          tableType={tableType}
        />

        <ForwardConfirmDialog
          open={openForwardModal}
          onClose={handleCloseForwardModal}
          onConfirm={handleForward}
          forwardConfirmMessage={forwardConfirmMessage}
          tableType={tableType}
        />
      </div>
    );
  }
);